import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchAdherence } from './flowPlannerApi';
import { responseHasActuals } from './flowPlannerAdherenceModel';

const POLL_INTERVAL_MS = 60 * 1000;

export default function useFlowPlannerActuals(date, planId) {
  // Actuals are on by default; the first fetch is driven by the mount effect below.
  const [visible, setVisible] = useState(true);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const inputsRef = useRef('');
  const visibleRef = useRef(true);
  inputsRef.current = `${date}|${planId}`;
  visibleRef.current = visible;

  const doFetch = useCallback(
    async (isRefresh) => {
      const inputs = `${date}|${planId}`;
      if (isRefresh) setRefreshing(true);
      else {
        setLoading(true);
        setError(null);
      }
      try {
        const resp = await fetchAdherence(date, planId);
        if (inputsRef.current !== inputs) return;
        if (isRefresh) {
          if (responseHasActuals(resp)) {
            setResponse(resp);
            setError(null);
          }
        } else {
          setResponse(resp || null);
          if (!responseHasActuals(resp)) setError('No actuals are available for this plan yet.');
        }
      } catch (e) {
        if (inputsRef.current !== inputs) return;
        if (!isRefresh) setError('Could not load actuals. Planned values are unaffected.');
      } finally {
        if (inputsRef.current === inputs) {
          if (isRefresh) setRefreshing(false);
          else setLoading(false);
        }
      }
    },
    [date, planId]
  );

  useEffect(() => {
    setResponse(null);
    setError(null);
    setLoading(false);
    setRefreshing(false);
    if (planId && visibleRef.current) doFetch(false);
  }, [date, planId, doFetch]);

  const toggle = useCallback(() => {
    const next = !visible;
    setVisible(next);
    if (!next) {
      // Hiding abandons the attempt, so its warning goes with it; showing again refetches.
      setError(null);
      return;
    }
    if (!planId || loading || refreshing) return;
    doFetch(responseHasActuals(response));
  }, [visible, planId, response, loading, refreshing, doFetch]);

  useEffect(() => {
    if (!visible || !planId) return undefined;
    const id = setInterval(() => doFetch(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [visible, planId, doFetch]);

  const dismissError = useCallback(() => {
    setError(null);
    if (!responseHasActuals(response)) {
      setVisible(false);
      setResponse(null);
    }
  }, [response]);

  return {
    showActuals: visible,
    response,
    loading,
    refreshing,
    error,
    toggle,
    dismissError
  };
}
